// Page Emploi - Design Indeed Style
// Split-view : Liste + Détail, Recherche avancée, Publication gratuite pour les pros

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, MapPin, Clock, Euro, Building2, Search, Filter,
  Plus, X, ChevronDown, Send, Eye, Heart, BookmarkPlus, Bookmark,
  Users, CheckCircle2, AlertCircle, Laptop, Calendar, Share2,
  Phone, Mail, FileText, ArrowLeft, ExternalLink, Zap, Star,
  ChevronRight, Home, Menu, User, TrendingUp, Award, Shield, Bell, BellRing, Trash2,
  SlidersHorizontal, Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import UniversalBanner from '../../components/layout/UniversalBanner';
import BetaBanner from '../../components/ui/BetaBanner';
import UnifiedFilterSystem, { FilterButton, FilterContextPhrase } from '../../components/filters/UnifiedFilterSystem';
import { useDemoMode } from '../../components/demo/DemoModeProvider';
import { JobSkeleton } from '../../components/ui/skeletons';

import { API_URL } from '../../config/api';

// Couleurs Indeed
const COLORS = {
  primary: '#2557a7',
  primaryHover: '#1a4a8f',
  success: '#22C55E',
  successHover: '#16A34A',
  urgent: '#d93025',
  new: '#2557a7',
  remote: '#0d47a1',
};

// Types de contrat
const JOB_TYPES = [
  { id: 'cdi', label: 'CDI', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'cdd', label: 'CDD', color: 'bg-blue-100 text-blue-700' },
  { id: 'interim', label: 'Intérim', color: 'bg-orange-100 text-orange-700' },
  { id: 'apprentissage', label: 'Apprentissage', color: 'bg-purple-100 text-purple-700' },
  { id: 'stage', label: 'Stage', color: 'bg-pink-100 text-pink-700' },
  { id: 'freelance', label: 'Freelance', color: 'bg-cyan-100 text-cyan-700' },
];

// Niveaux d'expérience
const EXPERIENCE_LEVELS = [
  { id: 'debutant', label: 'Débutant accepté' },
  { id: '1-3', label: '1-3 ans' },
  { id: '3-5', label: '3-5 ans' },
  { id: '5+', label: '5+ ans' },
];

// Métiers BTP
const METIERS = [
  'Électricien', 'Plombier', 'Maçon', 'Carreleur', 'Peintre', 'Menuisier',
  'Charpentier', 'Couvreur', 'Chauffagiste', 'Climaticien', 'Plaquiste',
  'Pisciniste', 'Terrassier', 'Paysagiste', 'Cuisiniste', 'Façadier',
  'Serrurier', 'Vitrier', 'Chef de chantier', 'Conducteur de travaux'
];

// Villes populaires
const POPULAR_CITIES = [
  'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Bordeaux', 'Nantes', 
  'Nice', 'Strasbourg', 'Montpellier', 'Lille'
];

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DE TEST - PRÉOUVERTURE EMPLOI
// ══════════════════════════════════════════════════════════════════════════════
const MOCK_JOBS = [
  {
    id: 'job-1',
    title: 'Électricien qualifié H/F',
    company: 'Elec Pro Services',
    city: 'Paris',
    job_type: 'cdi',
    salary_min: 2200,
    salary_max: 2800,
    salary_type: 'mensuel',
    experience: '1-3',
    description: 'Nous recherchons un électricien qualifié pour rejoindre notre équipe. Vous interviendrez sur des chantiers neufs et en rénovation. Permis B requis.',
    requirements: ['CAP/BEP Électricien', 'Habilitations électriques à jour', 'Permis B', '2 ans d\'expérience minimum'],
    benefits: ['Véhicule de service', 'Mutuelle entreprise', 'Primes chantiers', 'Formation continue'],
    is_urgent: true,
    remote_possible: false,
    created_at: new Date(Date.now() - 1 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-2',
    title: 'Plombier chauffagiste',
    company: 'Chauff\'Confort',
    city: 'Lyon',
    job_type: 'cdi',
    salary_min: 2400,
    salary_max: 3200,
    salary_type: 'mensuel',
    experience: '3-5',
    description: 'Rejoignez notre équipe spécialisée en installation et dépannage de systèmes de chauffage. Poste polyvalent avec évolution possible vers chef d\'équipe.',
    requirements: ['CAP/BEP Plomberie-Chauffage', 'Expérience PAC et chaudières gaz', 'Autonomie sur chantier'],
    benefits: ['13ème mois', 'Intéressement', 'Véhicule', 'Téléphone'],
    is_urgent: false,
    remote_possible: false,
    created_at: new Date(Date.now() - 2 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-3',
    title: 'Maçon coffreur bancheur',
    company: 'BTP Construction Sud',
    city: 'Marseille',
    job_type: 'cdi',
    salary_min: 2000,
    salary_max: 2600,
    salary_type: 'mensuel',
    experience: '1-3',
    description: 'Chantiers de gros œuvre en région PACA. Vous maîtrisez le coffrage et le ferraillage. Grands chantiers en cours.',
    requirements: ['Expérience coffrage traditionnel', 'Lecture de plans', 'CACES apprécié'],
    benefits: ['Paniers repas', 'Déplacements payés', 'Heures supplémentaires majorées'],
    is_urgent: true,
    remote_possible: false,
    created_at: new Date(Date.now() - 3 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-4',
    title: 'Peintre en bâtiment',
    company: 'Déco Habitat',
    city: 'Bordeaux',
    job_type: 'cdd',
    salary_min: 1900,
    salary_max: 2300,
    salary_type: 'mensuel',
    experience: 'debutant',
    description: 'CDD 6 mois renouvelable. Travaux de peinture intérieure et extérieure. Formation assurée pour profils motivés.',
    requirements: ['CAP Peintre applicateur de revêtements', 'Sens du détail', 'Permis B souhaité'],
    benefits: ['Possibilité CDI', 'Formation aux nouvelles techniques', 'Équipe jeune et dynamique'],
    is_urgent: false,
    remote_possible: false,
    created_at: new Date(Date.now() - 5 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-5',
    title: 'Chef de chantier TCE',
    company: 'Groupe Bâtisseurs',
    city: 'Toulouse',
    job_type: 'cdi',
    salary_min: 3500,
    salary_max: 4500,
    salary_type: 'mensuel',
    experience: '5+',
    description: 'Pilotage de chantiers tous corps d\'état. Management d\'équipes de 10 à 20 personnes. Reporting régulier à la direction.',
    requirements: ['BTS/DUT Bâtiment', '5 ans minimum en tant que chef de chantier', 'Maîtrise MS Project', 'Leadership'],
    benefits: ['Voiture de fonction', 'Prime objectifs', 'RTT', 'CE entreprise'],
    is_urgent: false,
    remote_possible: false,
    created_at: new Date(Date.now() - 7 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-6',
    title: 'Carreleur mosaïste',
    company: 'Art\'Carrelage',
    city: 'Nice',
    job_type: 'cdi',
    salary_min: 2100,
    salary_max: 2700,
    salary_type: 'mensuel',
    experience: '3-5',
    description: 'Spécialiste du carrelage haut de gamme et de la mosaïque. Clientèle exigeante sur la Côte d\'Azur.',
    requirements: ['CAP Carreleur', 'Expérience grands formats', 'Sens artistique pour mosaïque'],
    benefits: ['Primes qualité', 'Outillage fourni', 'Formation mosaïque Bisazza'],
    is_urgent: false,
    remote_possible: false,
    created_at: new Date(Date.now() - 10 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-7',
    title: 'Menuisier poseur ALU/PVC',
    company: 'Fenêtres Plus',
    city: 'Nantes',
    job_type: 'cdi',
    salary_min: 2000,
    salary_max: 2500,
    salary_type: 'mensuel',
    experience: '1-3',
    description: 'Pose de menuiseries extérieures chez les particuliers. Véhicule équipé fourni.',
    requirements: ['CAP Menuisier', 'Permis B obligatoire', 'Bon relationnel client'],
    benefits: ['Véhicule atelier', 'Primes pose', 'Mutuelle famille'],
    is_urgent: true,
    remote_possible: false,
    created_at: new Date(Date.now() - 4 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-8',
    title: 'Conducteur de travaux',
    company: 'Constructions Modernes',
    city: 'Strasbourg',
    job_type: 'cdi',
    salary_min: 4000,
    salary_max: 5000,
    salary_type: 'mensuel',
    experience: '5+',
    description: 'Gestion technique et financière de chantiers résidentiels. Relation client et coordination des sous-traitants.',
    requirements: ['Ingénieur ou équivalent', 'Expérience conducteur de travaux', 'Maîtrise budget chantier'],
    benefits: ['Voiture', 'Téléphone', 'Ordinateur', 'Intéressement'],
    is_urgent: false,
    remote_possible: true,
    created_at: new Date(Date.now() - 6 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-9',
    title: 'Apprenti plaquiste',
    company: 'Placo Express',
    city: 'Montpellier',
    job_type: 'apprentissage',
    salary_min: 800,
    salary_max: 1200,
    salary_type: 'mensuel',
    experience: 'debutant',
    description: 'Contrat d\'apprentissage pour devenir plaquiste. Formation en alternance avec notre équipe expérimentée.',
    requirements: ['Motivation', 'Rigueur', 'Ponctualité', '16-25 ans'],
    benefits: ['Formation diplômante', 'Embauche possible après diplôme', 'Aide au permis'],
    is_urgent: false,
    remote_possible: false,
    created_at: new Date(Date.now() - 8 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-10',
    title: 'Couvreur zingueur',
    company: 'Toitures Martin',
    city: 'Lille',
    job_type: 'cdi',
    salary_min: 2300,
    salary_max: 2900,
    salary_type: 'mensuel',
    experience: '3-5',
    description: 'Travaux de couverture traditionnelle et zinguerie. Interventions neuves et rénovation.',
    requirements: ['CAP Couvreur', 'Pas de vertige', 'Habilitation travail en hauteur'],
    benefits: ['EPI fournis', 'Prime grand froid', 'Paniers'],
    is_urgent: true,
    remote_possible: false,
    created_at: new Date(Date.now() - 2 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-11',
    title: 'Technicien SAV chauffage',
    company: 'Maintenance Plus',
    city: 'Paris',
    job_type: 'cdi',
    salary_min: 2500,
    salary_max: 3000,
    salary_type: 'mensuel',
    experience: '1-3',
    description: 'Dépannage et maintenance de chaudières et PAC. Astreintes rémunérées.',
    requirements: ['Formation chauffagiste', 'Attestation fluides frigorigènes', 'Sens du service'],
    benefits: ['Astreintes payées double', 'Véhicule', 'Tablette'],
    is_urgent: false,
    remote_possible: false,
    created_at: new Date(Date.now() - 1 * 24 * 3600000).toISOString()
  },
  {
    id: 'job-12',
    title: 'Façadier ITE',
    company: 'Iso Façades',
    city: 'Lyon',
    job_type: 'interim',
    salary_min: 14,
    salary_max: 18,
    salary_type: 'horaire',
    experience: '1-3',
    description: 'Mission longue durée. Pose d\'isolation thermique par l\'extérieur et finitions.',
    requirements: ['Expérience ITE', 'Travail en équipe', 'Permis B'],
    benefits: ['IFM + CP', 'Panier', 'Déplacement'],
    is_urgent: true,
    remote_possible: false,
    created_at: new Date(Date.now() - 1 * 24 * 3600000).toISOString()
  }
];

// Format date relative
const formatRelativeDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Hier";
  if (diff < 7) return `Il y a ${diff} jours`;
  if (diff < 30) return `Il y a ${Math.floor(diff / 7)} semaines`;
  return date.toLocaleDateString('fr-FR');
};

// Format salaire
const formatSalary = (job) => {
  if (!job.salary_min && !job.salary_max) return null;
  const suffix = job.salary_type === 'annuel' ? '/an' : job.salary_type === 'horaire' ? '/h' : '/mois';
  if (job.salary_min && job.salary_max) {
    return `${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()} €${suffix}`;
  }
  return `${(job.salary_min || job.salary_max).toLocaleString()} €${suffix}`;
};

// ============ COMPOSANT CARTE OFFRE (Liste) ============
function JobListCard({ job, isSelected, onClick, onSave, isSaved, isTestData = false }) {
  const jobType = JOB_TYPES.find(t => t.id === job.job_type) || JOB_TYPES[0];
  const isNew = job.created_at && (new Date() - new Date(job.created_at)) < 3 * 24 * 60 * 60 * 1000;
  const salary = formatSalary(job);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`relative bg-white rounded-xl border-2 p-4 cursor-pointer transition-all ${
        isSelected 
          ? 'border-[#2557a7] shadow-lg' 
          : 'border-transparent hover:border-gray-200 hover:shadow-md'
      }`}
      style={{ boxShadow: isSelected ? '0 4px 20px rgba(37,87,167,0.15)' : undefined }}
      data-testid={`job-card-${job.id}`}
    >
      {/* Badge TEST (données de préouverture) */}
      {isTestData && (
        <div className="absolute top-2 right-2 px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
          TEST
        </div>
      )}
      
      {/* Badges */}
      <div className="flex items-center gap-2 mb-3">
        {job.is_urgent && (
          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
            URGENT
          </span>
        )}
        {isNew && (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded">
            NOUVEAU
          </span>
        )}
        {job.remote_possible && (
          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded flex items-center gap-1">
            <Laptop size={12} /> Télétravail
          </span>
        )}
      </div>

      {/* Titre */}
      <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-2 hover:text-[#2557a7]">
        {job.title}
      </h3>

      {/* Entreprise */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-700 font-medium">
          {job.company_name || job.author?.company_name || job.author?.display_name || 'Entreprise'}
        </span>
        {job.company_verified && (
          <Shield size={14} className="text-blue-500" />
        )}
      </div>

      {/* Localisation */}
      <div className="flex items-center gap-1 text-gray-500 text-sm mb-3">
        <MapPin size={14} />
        <span>{job.city}{job.postal_code ? ` (${job.postal_code})` : ''}</span>
      </div>

      {/* Salaire (mis en avant comme Indeed) */}
      {salary && (
        <div className="mb-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg font-semibold text-sm">
            <Euro size={14} />
            {salary}
          </span>
        </div>
      )}

      {/* Infos rapides */}
      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`px-2 py-1 rounded text-xs font-medium ${jobType.color}`}>
          {jobType.label}
        </span>
        {job.experience_required && (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
            {job.experience_required}
          </span>
        )}
        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs flex items-center gap-1">
          <Briefcase size={12} />
          {job.metier}
        </span>
      </div>

      {/* Extrait description */}
      <p className="text-gray-600 text-sm line-clamp-2 mb-3">
        {job.description}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-400">
          {formatRelativeDate(job.created_at)}
        </span>
        
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onSave(job); }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title={isSaved ? 'Retirer des favoris' : 'Sauvegarder'}
          >
            {isSaved ? (
              <Bookmark size={18} className="text-[#2557a7] fill-[#2557a7]" />
            ) : (
              <BookmarkPlus size={18} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Indicateur sélection */}
      {isSelected && (
        <div className="absolute left-0 top-4 bottom-4 w-1 bg-[#2557a7] rounded-r" />
      )}
    </motion.div>
  );
}

// ============ COMPOSANT DÉTAIL OFFRE (Panel droit) ============
function JobDetailPanel({ job, onApply, onSave, isSaved, isLoggedIn, onClose }) {
  const { requireAuth } = useDemoMode();
  const jobType = JOB_TYPES.find(t => t.id === job?.job_type) || JOB_TYPES[0];
  const salary = job ? formatSalary(job) : null;
  const isNew = job?.created_at && (new Date() - new Date(job.created_at)) < 3 * 24 * 60 * 60 * 1000;

  const handleApply = () => {
    if (requireAuth('postuler à cette offre')) {
      onApply(job);
    }
  };

  const handleSave = () => {
    if (requireAuth('sauvegarder cette offre')) {
      onSave(job);
    }
  };

  if (!job) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8">
        <Briefcase size={64} className="mb-4 opacity-50" />
        <p className="text-lg font-medium">Sélectionnez une offre</p>
        <p className="text-sm">Cliquez sur une offre pour voir les détails</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header sticky */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <X size={20} />
        </button>

        {/* Badges */}
        <div className="flex items-center gap-2 mb-3">
          {job.is_urgent && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
              URGENT
            </span>
          )}
          {isNew && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded">
              NOUVEAU
            </span>
          )}
        </div>

        {/* Titre */}
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 pr-8 lg:pr-0">
          {job.title}
        </h1>

        {/* Entreprise */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2557a7] to-[#1a4a8f] flex items-center justify-center flex-shrink-0">
            {job.company_logo ? (
              <img src={job.company_logo} alt="" className="w-8 h-8 object-contain" />
            ) : (
              <Building2 size={24} className="text-white" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 flex items-center gap-1">
              {job.company_name || job.author?.company_name || 'Entreprise'}
              {job.company_verified && <Shield size={14} className="text-blue-500" />}
            </p>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin size={12} />
              {job.city}{job.postal_code ? ` (${job.postal_code})` : ''}
            </p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-2">
          <Button
            onClick={handleApply}
            className="flex-1 bg-[#2557a7] hover:bg-[#1a4a8f] text-white font-semibold rounded-xl h-11"
            data-testid="apply-btn"
          >
            <Send size={16} className="mr-2" />
            Postuler maintenant
          </Button>
          <button
            onClick={handleSave}
            className={`h-11 w-11 rounded-xl border-2 flex items-center justify-center transition-colors ${
              isSaved 
                ? 'border-[#2557a7] bg-[#2557a7]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {isSaved ? (
              <Bookmark size={20} className="text-[#2557a7] fill-[#2557a7]" />
            ) : (
              <BookmarkPlus size={20} className="text-gray-400" />
            )}
          </button>
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              toast.success('Lien copié !');
            }}
            className="h-11 w-11 rounded-xl border-2 border-gray-200 hover:border-gray-300 flex items-center justify-center"
          >
            <Share2 size={20} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* Content scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Infos clés */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {salary && (
            <div className="bg-emerald-50 rounded-xl p-3">
              <p className="text-xs text-emerald-600 mb-1">Salaire</p>
              <p className="font-bold text-emerald-700">{salary}</p>
            </div>
          )}
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-500 mb-1">Type de contrat</p>
            <p className="font-semibold text-gray-900">{jobType.label}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-500 mb-1">Métier</p>
            <p className="font-semibold text-gray-900">{job.metier}</p>
          </div>
          {job.experience_required && (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">Expérience</p>
              <p className="font-semibold text-gray-900">{job.experience_required}</p>
            </div>
          )}
        </div>

        {/* Avantages */}
        {job.remote_possible && (
          <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-xl mb-6">
            <Laptop size={20} className="text-indigo-600" />
            <span className="font-medium text-indigo-700">Télétravail possible</span>
          </div>
        )}

        {/* Description */}
        <div className="mb-6">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-[22px] md:text-[18px]">
            <FileText size={18} className="text-[#2557a7]" />
            Description du poste
          </h3>
          <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">
            {job.description}
          </div>
        </div>

        {/* Profil recherché (si disponible) */}
        {job.requirements && (
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-[22px] md:text-[18px]">
              <CheckCircle2 size={18} className="text-[#2557a7]" />
              Profil recherché
            </h3>
            <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">
              {job.requirements}
            </div>
          </div>
        )}

        {/* Contact - Protégé */}
        {(job.contact_email || job.contact_phone) && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h2 className="font-bold text-gray-900 mb-3">Contact</h2>
            <div className="space-y-2">
              {job.contact_email && (
                <button 
                  onClick={() => {
                    if (requireAuth("voir l'email de contact")) {
                      window.location.href = `mailto:${job.contact_email}`;
                    }
                  }}
                  className="flex items-center gap-2 text-[#2557a7] hover:underline"
                >
                  <Mail size={16} />
                  <span className="blur-[3px] hover:blur-none transition-all">contact@*****.com</span>
                </button>
              )}
              {job.contact_phone && (
                <button 
                  onClick={() => {
                    if (requireAuth("voir le téléphone de contact")) {
                      window.location.href = `tel:${job.contact_phone}`;
                    }
                  }}
                  className="flex items-center gap-2 text-[#2557a7] hover:underline"
                >
                  <Phone size={16} />
                  <span className="blur-[3px] hover:blur-none transition-all">06 ** ** ** **</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-gray-500 border-t border-gray-100 pt-4">
          <span className="flex items-center gap-1">
            <Eye size={14} />
            {job.views_count || 0} vues
          </span>
          <span className="flex items-center gap-1">
            <Users size={14} />
            {job.applications_count || 0} candidatures
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={14} />
            Publié {formatRelativeDate(job.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============ MODAL CANDIDATURE ============
function ApplyModal({ job, onClose, onSubmit, isLoggedIn }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    message: '',
    email: '',
    phone: '',
    cv_url: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      toast.error('Connectez-vous pour postuler');
      navigate('/connexion');
      return;
    }
    if (!formData.message) {
      toast.error('Veuillez ajouter un message de motivation');
      return;
    }
    setLoading(true);
    await onSubmit(job.id, formData);
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Postuler</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} />
            </button>
          </div>

          {/* Info offre */}
          <div className="p-4 bg-[#2557a7]/5 rounded-xl mb-6 border border-[#2557a7]/20">
            <h3 className="font-semibold text-gray-900">{job.title}</h3>
            <p className="text-sm text-gray-600">
              {job.company_name || job.author?.display_name} • {job.city}
            </p>
          </div>

          {!isLoggedIn ? (
            <div className="text-center py-6">
              <AlertCircle size={48} className="mx-auto text-orange-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Connexion requise</h3>
              <p className="text-gray-500 mb-4">Connectez-vous pour postuler à cette offre</p>
              <Button
                onClick={() => navigate('/connexion')}
                className="bg-[#2557a7] hover:bg-[#1a4a8f] rounded-xl"
              >
                Se connecter
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message de motivation *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2557a7] resize-none"
                  placeholder="Présente-toi et explique pourquoi ce poste t'intéresse..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="votre@email.com"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lien vers ton CV (optionnel)
                </label>
                <Input
                  type="url"
                  value={formData.cv_url}
                  onChange={(e) => setFormData({ ...formData, cv_url: e.target.value })}
                  placeholder="https://..."
                  className="rounded-xl"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#2557a7] hover:bg-[#1a4a8f] rounded-xl h-12 font-semibold"
                disabled={loading}
              >
                {loading ? 'Envoi en cours...' : 'Envoyer ma candidature'}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ MODAL CRÉATION OFFRE ============
function CreateJobModal({ onClose, onSubmit }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    metier: '',
    job_type: 'cdi',
    experience_required: '',
    salary_min: '',
    salary_max: '',
    salary_type: 'mensuel',
    city: '',
    postal_code: '',
    remote_possible: false,
    is_urgent: false,
    company_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.metier || !formData.city) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setLoading(true);
    await onSubmit({
      ...formData,
      salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
      salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null
    });
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#2557a7] to-[#1a4a8f]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Publier une offre d'emploi</h2>
              <p className="text-white/70 text-sm mt-1">Publication gratuite pour les professionnels</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white">
              <X size={20} />
            </button>
          </div>
          
          {/* Steps */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s ? 'bg-white text-[#2557a7]' : 'bg-white/20 text-white/70'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-white' : 'bg-white/20'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre du poste *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Électricien qualifié N3P2"
                    className="rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Métier *
                    </label>
                    <select
                      value={formData.metier}
                      onChange={(e) => setFormData({ ...formData, metier: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2557a7] bg-white"
                    >
                      <option value="">Sélectionner...</option>
                      {METIERS.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de contrat *
                    </label>
                    <select
                      value={formData.job_type}
                      onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2557a7] bg-white"
                    >
                      {JOB_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description du poste *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2557a7] resize-none"
                    placeholder="Décrivez le poste, les missions, l'environnement de travail..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profil recherché
                  </label>
                  <textarea
                    value={formData.requirements}
                    onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2557a7] resize-none"
                    placeholder="Compétences requises, certifications, qualités..."
                  />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expérience requise
                  </label>
                  <select
                    value={formData.experience_required}
                    onChange={(e) => setFormData({ ...formData, experience_required: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2557a7] bg-white"
                  >
                    <option value="">Non spécifié</option>
                    {EXPERIENCE_LEVELS.map(e => (
                      <option key={e.id} value={e.label}>{e.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rémunération
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      type="number"
                      value={formData.salary_min}
                      onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                      placeholder="Min"
                      className="rounded-xl"
                    />
                    <Input
                      type="number"
                      value={formData.salary_max}
                      onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                      placeholder="Max"
                      className="rounded-xl"
                    />
                    <select
                      value={formData.salary_type}
                      onChange={(e) => setFormData({ ...formData, salary_type: e.target.value })}
                      className="px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2557a7] bg-white"
                    >
                      <option value="mensuel">€/mois</option>
                      <option value="annuel">€/an</option>
                      <option value="horaire">€/h</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville *
                    </label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Paris"
                      className="rounded-xl"
                      list="cities"
                    />
                    <datalist id="cities">
                      {POPULAR_CITIES.map(c => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal
                    </label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="75001"
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.remote_possible}
                      onChange={(e) => setFormData({ ...formData, remote_possible: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-[#2557a7] focus:ring-[#2557a7]"
                    />
                    <Laptop size={20} className="text-gray-400" />
                    <span className="text-gray-700">Télétravail possible</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.is_urgent}
                      onChange={(e) => setFormData({ ...formData, is_urgent: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <Zap size={20} className="text-red-500" />
                    <span className="text-gray-700">Recrutement urgent</span>
                  </label>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Informations de contact</h3>
                  <p className="text-sm text-gray-500">Ces informations seront visibles par les candidats</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'entreprise
                  </label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="SARL Martin Construction"
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email de contact
                  </label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="recrutement@entreprise.com"
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <Input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="01 23 45 67 89"
                    className="rounded-xl"
                  />
                </div>

                {/* Résumé */}
                <div className="bg-[#2557a7]/5 rounded-xl p-4 border border-[#2557a7]/20">
                  <h3 className="font-semibold text-gray-900 mb-3">Récapitulatif</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Poste:</span> <span className="font-medium">{formData.title || '-'}</span></p>
                    <p><span className="text-gray-500">Métier:</span> <span className="font-medium">{formData.metier || '-'}</span></p>
                    <p><span className="text-gray-500">Contrat:</span> <span className="font-medium">{JOB_TYPES.find(t => t.id === formData.job_type)?.label}</span></p>
                    <p><span className="text-gray-500">Lieu:</span> <span className="font-medium">{formData.city || '-'}</span></p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="rounded-xl"
            >
              Retour
            </Button>
          ) : (
            <div />
          )}
          
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="bg-[#2557a7] hover:bg-[#1a4a8f] rounded-xl"
            >
              Continuer
              <ChevronRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
            >
              {loading ? 'Publication...' : 'Publier l\'offre'}
              <CheckCircle2 size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ MODAL CRÉATION ALERTE ============
function CreateAlertModal({ onClose, currentFilters, isLoggedIn }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: currentFilters.query ? `Alerte "${currentFilters.query}"` : 'Mon alerte emploi',
    keywords: currentFilters.query || '',
    metiers: currentFilters.metier ? [currentFilters.metier] : [],
    job_types: currentFilters.type ? [currentFilters.type] : [],
    cities: currentFilters.city ? [currentFilters.city] : [],
    salary_min: '',
    remote_only: false,
    frequency: 'instant'
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('buyer_token') || localStorage.getItem('ecosystem_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLoggedIn) {
      toast.error('Connectez-vous pour créer une alerte');
      navigate('/connexion');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/job-alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          salary_min: formData.salary_min ? parseInt(formData.salary_min) : null
        })
      });

      if (res.ok) {
        toast.success('Alerte créée !', {
          description: 'Tu recevras un email dès qu\'une offre correspond à tes critères.'
        });
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la création');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const toggleJobType = (typeId) => {
    setFormData(prev => ({
      ...prev,
      job_types: prev.job_types.includes(typeId)
        ? prev.job_types.filter(t => t !== typeId)
        : [...prev.job_types, typeId]
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#2557a7] to-[#1a4a8f]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <BellRing size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Créer une alerte</h2>
                <p className="text-white/70 text-sm">Recevez les offres par email</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white">
              <X size={20} />
            </button>
          </div>
        </div>

        {!isLoggedIn ? (
          <div className="p-8 text-center">
            <Bell size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">Connexion requise</h3>
            <p className="text-gray-500 mb-6">
              Connectez-vous pour créer une alerte et recevoir les offres par email.
            </p>
            <Button
              onClick={() => navigate('/connexion')}
              className="bg-[#2557a7] hover:bg-[#1a4a8f] rounded-xl"
            >
              Se connecter
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Nom de l'alerte */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'alerte
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Électricien Paris"
                className="rounded-xl"
              />
            </div>

            {/* Mots-clés */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mots-clés (optionnel)
              </label>
              <Input
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="Ex: électricien, câblage, tertiaire..."
                className="rounded-xl"
              />
            </div>

            {/* Types de contrat */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Types de contrat
              </label>
              <div className="flex flex-wrap gap-2">
                {JOB_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggleJobType(type.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      formData.job_types.includes(type.id)
                        ? 'border-[#2557a7] bg-[#2557a7] text-white'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Ville */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ville (optionnel)
              </label>
              <Input
                value={formData.cities[0] || ''}
                onChange={(e) => setFormData({ ...formData, cities: e.target.value ? [e.target.value] : [] })}
                placeholder="Ex: Paris, Lyon..."
                list="alert-cities"
                className="rounded-xl"
              />
              <datalist id="alert-cities">
                {POPULAR_CITIES.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>

            {/* Salaire minimum */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Salaire minimum (€/mois)
              </label>
              <Input
                type="number"
                value={formData.salary_min}
                onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                placeholder="Ex: 2500"
                className="rounded-xl"
              />
            </div>

            {/* Télétravail uniquement */}
            <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.remote_only}
                onChange={(e) => setFormData({ ...formData, remote_only: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-[#2557a7] focus:ring-[#2557a7]"
              />
              <Laptop size={20} className="text-indigo-500" />
              <span className="text-gray-700">Télétravail uniquement</span>
            </label>

            {/* Info */}
            <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
              <Bell size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                Tu recevras un email instantanément dès qu'une nouvelle offre correspond à tes critères.
              </p>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2557a7] hover:bg-[#1a4a8f] rounded-xl h-12 font-semibold"
            >
              {loading ? 'Création...' : 'Créer mon alerte'}
            </Button>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

// ============ PAGE PRINCIPALE ============
export default function JobsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  
  // Search & filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchCity, setSearchCity] = useState(searchParams.get('city') || '');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || '');
  const [selectedMetier, setSelectedMetier] = useState(searchParams.get('metier') || '');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  
  // Auth
  const token = localStorage.getItem('buyer_token') || localStorage.getItem('ecosystem_token');
  const isLoggedIn = !!token;
  
  // Check if user is pro (can publish)
  const [userProfile, setUserProfile] = useState(null);
  const isPro = userProfile?.user_type === 'professionnel' || userProfile?.is_seller;

  useEffect(() => {
    // Load saved jobs from localStorage
    const saved = localStorage.getItem('savedJobs');
    if (saved) setSavedJobs(JSON.parse(saved));
    
    // Load user profile if logged in
    if (token) {
      fetch(`${API_URL}/api/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) setUserProfile(data);
        })
        .catch(() => {});
    }
  }, [token]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      // Construire l'URL avec les filtres
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (searchCity) params.set('location', searchCity);
      if (selectedType) params.set('job_type', selectedType);
      if (selectedMetier) params.set('metier', selectedMetier.toLowerCase());
      params.set('limit', '30');
      
      const response = await fetch(`${API_URL}/api/emploi/offres?${params.toString()}`);
      
      if (response.ok) {
        const data = await response.json();
        // Mapper les données de l'API au format attendu par le frontend
        const mappedJobs = (data.offres || []).map(offre => ({
          id: offre.id,
          title: offre.titre || offre.title,
          company: offre.company?.name || offre.entreprise?.name || 'Entreprise',
          company_name: offre.company?.name || offre.entreprise?.name || 'Entreprise',
          company_logo: offre.company?.logo || offre.entreprise?.logo,
          company_verified: offre.company?.verified || offre.entreprise?.verified || false,
          city: offre.location || offre.localisation || '',
          job_type: offre.job_type || offre.type_contrat || 'cdi',
          salary_min: offre.salary_min || offre.salaire_min,
          salary_max: offre.salary_max || offre.salaire_max,
          salary_type: offre.salary_type === 'monthly' ? 'mensuel' : offre.salary_type,
          experience: offre.experience_required || offre.experience,
          description: offre.description || '',
          requirements: offre.skills || offre.competences || [],
          benefits: offre.benefits || offre.avantages || [],
          is_urgent: offre.urgent || false,
          remote_possible: offre.remote_policy === 'remote' || offre.remote_policy === 'hybrid',
          views: offre.views || offre.vues || 0,
          applications: offre.applications || offre.candidatures || 0,
          created_at: offre.created_at || offre.date_publication
        }));
        
        setJobs(mappedJobs);
        
        // Auto-select first job on desktop
        if (mappedJobs.length > 0 && window.innerWidth >= 1024) {
          setSelectedJob(mappedJobs[0]);
        }
      } else {
        console.error('Erreur API emploi:', response.status);
        setJobs([]);
      }
    } catch (e) {
      console.error('Error fetching jobs:', e);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchCity, selectedType, selectedMetier]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (searchCity) params.set('city', searchCity);
    if (selectedType) params.set('type', selectedType);
    if (selectedMetier) params.set('metier', selectedMetier);
    setSearchParams(params);
  };

  const handleSaveJob = (job) => {
    const isSaved = savedJobs.includes(job.id);
    let newSaved;
    if (isSaved) {
      newSaved = savedJobs.filter(id => id !== job.id);
      toast.success('Offre retirée des favoris');
    } else {
      newSaved = [...savedJobs, job.id];
      toast.success('Offre sauvegardée !');
    }
    setSavedJobs(newSaved);
    localStorage.setItem('savedJobs', JSON.stringify(newSaved));
  };

  const handleCreateJob = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        toast.success('Offre publiée avec succès !');
        setShowCreateModal(false);
        fetchJobs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la publication');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };

  const handleApply = async (jobId, data) => {
    if (!isLoggedIn) {
      toast.error('Connectez-vous pour postuler');
      navigate('/connexion');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/jobs/${jobId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        toast.success('Candidature envoyée avec succès !');
        setShowApplyModal(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'envoi');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    // On mobile, show detail panel
    if (window.innerWidth < 1024) {
      setShowMobileDetail(true);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSearchCity('');
    setSelectedType('');
    setSelectedMetier('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="jobs-page">
      {/* Header Global Unifié */}
      <UnifiedHeader onSearch={({ query, city }) => {
        if (query) setSearchQuery(query);
        if (city) setSearchCity(city);
        handleSearch({ preventDefault: () => {} });
      }} />
      
      {/* Navigation Principale */}
      <MainNavigation />

      {/* Spacer Mobile - 44px pour le menu horizontal fixed */}
      <div className="h-11 md:hidden" />

      {/* Spacer Desktop/Tablet - pour compenser les headers fixed + sous-menu */}
      <div className="hidden md:block" style={{ height: '100px' }} />

      {/* ═══ BANNIÈRE EMPLOI MOBILE - Premium Apple/Stripe ═══ */}
      <div className="md:hidden bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        <div className="px-4 pt-6 pb-3">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white whitespace-nowrap leading-none">
              Trouve un emploi 100% BTP
            </h1>
            <p className="text-white/90 text-sm font-normal whitespace-nowrap leading-none mt-2">
              En <span className="font-semibold">3 clics</span> auprès des <span className="font-semibold">entreprises du bâtiment</span>
            </p>
          </div>
        </div>
      </div>

      {/* Badges de confiance - Masqué sur Desktop */}

      {/* Filtres actifs - Ligne discrète */}
      {(selectedType || searchCity || selectedMetier) && (
        <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto bg-gray-50" style={{ scrollbarWidth: 'none' }}>
          {selectedType && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {JOB_TYPES.find(t => t.id === selectedType)?.label}
              <button onClick={() => setSelectedType('')} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {searchCity && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {searchCity}
              <button onClick={() => setSearchCity('')} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {selectedMetier && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {selectedMetier}
              <button onClick={() => setSelectedMetier('')} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          <button
            onClick={() => {
              setSelectedType('');
              setSearchCity('');
              setSelectedMetier('');
            }}
            className="text-xs text-gray-500 hover:text-red-600 font-medium whitespace-nowrap ml-1"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          SYSTÈME DE FILTRES UNIFIÉ - Amazon/Airbnb Style (identique à Artisans)
      ══════════════════════════════════════════════════════════════════════════ */}
      <UnifiedFilterSystem
        context="emploi"
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={{ contrat: selectedType, ville: searchCity, metier: selectedMetier }}
        onFiltersChange={(newFilters) => {
          if (newFilters.contrat !== undefined) setSelectedType(newFilters.contrat || '');
          if (newFilters.ville !== undefined) setSearchCity(newFilters.ville || '');
          if (newFilters.metier !== undefined) setSelectedMetier(newFilters.metier || '');
        }}
        resultsCount={jobs.length}
        onApply={() => setShowFilters(false)}
      />

      {/* Main content - Split view */}
      <main className="max-w-7xl mx-auto">
        <div className="flex">
          {/* Left panel - Job list */}
          <div className={`w-full lg:w-[420px] xl:w-[480px] flex-shrink-0 ${showMobileDetail ? 'hidden lg:block' : ''}`}>
            {/* Results count */}
            <div className="px-4 py-3 bg-white/80 backdrop-blur-sm sticky top-[180px] z-10 border-b border-gray-200">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{jobs.length}</span> offres d'emploi
                {(searchQuery || searchCity) && (
                  <span> pour "{searchQuery}{searchCity ? ` à ${searchCity}` : ''}"</span>
                )}
              </p>
            </div>

            {/* Jobs list */}
            <div className="p-4 space-y-3">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <JobSkeleton />
                    </motion.div>
                  ))}
                </div>
              ) : jobs.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center">
                  <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Aucune offre trouvée</h3>
                  <p className="text-gray-500 mb-4">
                    Modifie tes critères de recherche ou crée une alerte
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button onClick={clearFilters} className="text-[#2557a7] font-medium hover:underline">
                      Voir toutes les offres
                    </button>
                    <Button
                      onClick={() => setShowAlertModal(true)}
                      className="bg-[#2557a7] hover:bg-[#1a4a8f] rounded-lg"
                    >
                      <Bell size={16} className="mr-2" />
                      Créer une alerte
                    </Button>
                  </div>
                </div>
              ) : (
                jobs.map((job) => (
                  <JobListCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJob?.id === job.id}
                    isTestData={true}
                    onClick={() => handleSelectJob(job)}
                    onSave={handleSaveJob}
                    isSaved={savedJobs.includes(job.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right panel - Job detail (Desktop) */}
          <div className="hidden lg:block flex-1 sticky top-[180px] h-[calc(100vh-180px)] bg-white border-l border-gray-200">
            <JobDetailPanel
              job={selectedJob}
              onApply={(job) => setShowApplyModal(job)}
              onSave={handleSaveJob}
              isSaved={selectedJob ? savedJobs.includes(selectedJob.id) : false}
              isLoggedIn={isLoggedIn}
              onClose={() => {}}
            />
          </div>
        </div>
      </main>

      {/* Mobile detail panel */}
      <AnimatePresence>
        {showMobileDetail && selectedJob && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 bg-white z-50 lg:hidden overflow-y-auto"
          >
            <JobDetailPanel
              job={selectedJob}
              onApply={(job) => setShowApplyModal(job)}
              onSave={handleSaveJob}
              isSaved={savedJobs.includes(selectedJob.id)}
              isLoggedIn={isLoggedIn}
              onClose={() => setShowMobileDetail(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB Mobile pour les pros */}
      {isPro && !showMobileDetail && (
        <button
          onClick={() => setShowCreateModal(true)}
          className="fixed bottom-6 right-4 z-40 w-14 h-14 rounded-full bg-[#2557a7] text-white shadow-xl flex items-center justify-center lg:hidden"
          data-testid="fab-create-job"
        >
          <Plus size={26} />
        </button>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateJobModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateJob}
          />
        )}
        
        {showApplyModal && (
          <ApplyModal
            job={showApplyModal}
            onClose={() => setShowApplyModal(null)}
            onSubmit={handleApply}
            isLoggedIn={isLoggedIn}
          />
        )}
        
        {showAlertModal && (
          <CreateAlertModal
            onClose={() => setShowAlertModal(false)}
            currentFilters={{
              query: searchQuery,
              city: searchCity,
              type: selectedType,
              metier: selectedMetier
            }}
            isLoggedIn={isLoggedIn}
          />
        )}
      </AnimatePresence>

      {/* Styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
